import { type Clip } from '@air/api/types';
import { type SessionArtifactRow } from '@air/server-sdk';

import { GalleryItemType } from '~/components/Gallery/types';
import { type PrivateWorkspaceAgentClipsListByIdsRows } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentClipsListByIds';
import { type UseGalleryMixedAsset } from '~/swr-hooks/gallery/types';
import { getAssetGalleryItemType } from '~/utils/AssetUtils';

export type WorkspaceAgentAssetListRow = Extract<SessionArtifactRow, { type: 'assetList' }>;

export type WorkspaceAgentAssetListClipRow = PrivateWorkspaceAgentClipsListByIdsRows[number];

export type ToWorkspaceAgentAssetListItemsParams = {
  /** Rows whose clip lookup has settled. Rows still waiting on one would read as unavailable. */
  rows: readonly WorkspaceAgentAssetListRow[];
  clips: readonly WorkspaceAgentAssetListClipRow[];
};

export type WorkspaceAgentAssetListItems = {
  /** Every resolved clip, in row order. */
  clips: Clip[];
  /** Image/video cards, in row order (gallery's assets section). */
  assets: Clip[];
  /** Everything else, in row order (gallery's files section). */
  files: Clip[];
  /** Assets and files interleaved in row order (table rows). */
  mixedItems: UseGalleryMixedAsset<Clip>[];
  /** Resolved plus unavailable rows. */
  totalCount: number;
  /** Rows with no version id, or whose clip the workspace no longer returns. */
  unavailableCount: number;
};

/** The artifact row's version id is the clip id — the same mapping the list pill uses. */
export const getWorkspaceAgentAssetListRowClipIds = (rows: readonly WorkspaceAgentAssetListRow[]): string[] =>
  rows.flatMap((row) => (row.payload.versionId ? [row.payload.versionId] : []));

/**
 * `Clips.listByIds` leaves out these fields. Filled with their empty values so the shared cards
 * can take the row: a card's favorite toggle and proxy-download submenu read them.
 */
const toWorkspaceAgentAssetListClip = (row: WorkspaceAgentAssetListClipRow): Clip => ({
  ...row,
  altResolutions: [],
  bookmarked: false,
  smartTags: [],
});

export const toWorkspaceAgentAssetListItems = ({
  clips,
  rows,
}: ToWorkspaceAgentAssetListItemsParams): WorkspaceAgentAssetListItems => {
  const clipsById = new Map(clips.map((clip) => [clip.id, clip]));
  const orderedRows = [...rows].sort((left, right) => left.position - right.position);
  const seenClipIds = new Set<string>();
  const result: WorkspaceAgentAssetListItems = {
    assets: [],
    clips: [],
    files: [],
    mixedItems: [],
    totalCount: 0,
    unavailableCount: 0,
  };

  for (const row of orderedRows) {
    const clipId = row.payload.versionId;

    // A repeated clip would render as two cards with one key.
    if (clipId && seenClipIds.has(clipId)) {
      continue;
    }

    const clipRow = clipId ? clipsById.get(clipId) : undefined;

    result.totalCount += 1;

    if (!clipId || !clipRow) {
      result.unavailableCount += 1;
      continue;
    }

    seenClipIds.add(clipId);

    const clip = toWorkspaceAgentAssetListClip(clipRow);
    const type = getAssetGalleryItemType(clip) === GalleryItemType.file ? 'file' : 'asset';

    result.clips.push(clip);
    result.mixedItems.push({ item: clip, type });

    if (type === 'file') {
      result.files.push(clip);
    } else {
      result.assets.push(clip);
    }
  }

  return result;
};
